import Edit16 from '@carbon/icons-react/es/edit/16';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { Field } from 'api/tasks';
import { Menu } from 'components/atoms/dropdown';
import { confirm as ModalAtom } from 'components/atoms/modal';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslation } from 'hooks/use-translation';
import { useDeleteFieldMutation } from 'queries';
import { useIntervals } from 'utils/project-fields/use-project-fields';
import { getKeyeventFieldEditUrl, getIntervalFieldEditUrl } from 'utils/urls';

export const MoreOption = ({
  field,
  dataCy,
}: {
  field: Field;
  dataCy?: string;
}): JSX.Element | null => {
  const { fieldId, fieldType } = field;
  const { mutate } = useDeleteFieldMutation(fieldId);
  const history = useHistory();
  const cannotDeleteTitle = useTranslation(
    'settings.events.cannot_delete.title',
  );
  const cannotDeleteDescription = useTranslation(
    'settings.events.cannot_delete.description',
  );
  const confirm = useDeleteConfirmModal({
    title: useTranslation('settings.fields.delete.confirm.title'),
    content: useTranslation('settings.fields.delete.confirm.message'),
    onOk: () => mutate({}),
  });
  const intervals = useIntervals();
  const intervalsHavingKeyEvents = intervals.reduce(
    (acc: number[], curr: Field) => {
      const startKeyEventId = curr.metaData?.startKeyEventId ?? 0;
      const endKeyEventId = curr.metaData?.endKeyEventId ?? 0;

      if (startKeyEventId !== 0 && !acc.includes(startKeyEventId)) {
        acc = [...acc, startKeyEventId];
      }
      if (endKeyEventId !== 0 && !acc.includes(endKeyEventId)) {
        acc = [...acc, endKeyEventId];
      }

      return acc;
    },
    [],
  );

  const handleKeyEventsDelete = (fieldID) => {
    if (intervalsHavingKeyEvents.includes(fieldID)) {
      ModalAtom({
        okText: 'Ok',
        onCancel: () => {},
        onOk: () => {},
        title: cannotDeleteTitle,
        content: cannotDeleteDescription,
      });
    } else {
      confirm();
    }
  };

  const handleDelete = (fieldTypeValue, fieldID) => {
    if (fieldTypeValue === 'KEY_EVENTS') {
      handleKeyEventsDelete(fieldID);
    }
    if (fieldTypeValue === 'INTERVALS') {
      confirm();
    }
  };

  const menu = (
    <Menu>
      <Menu.Header data-cy={`${dataCy}.edit`}>
        <FormattedMessage id="more_actions" />
      </Menu.Header>
      <Menu.Item
        key="edit"
        icon={<Edit16 />}
        onClick={() =>
          history.push(
            fieldType === 'KEY_EVENTS'
              ? getKeyeventFieldEditUrl(fieldId)
              : fieldType === 'INTERVALS'
              ? getIntervalFieldEditUrl(fieldId)
              : '',
          )
        }
      >
        <FormattedMessage id="edit" />
      </Menu.Item>
      <Menu.ItemDelete
        data-cy={`${dataCy}.delete`}
        key="delete"
        onClick={() => handleDelete(fieldType, fieldId)}
      />
    </Menu>
  );

  return <MoreActionsDropdown dataCyValue={dataCy} menu={menu} />;
};
